import Phaser from "phaser";
import { StageConfig } from "../data/StageConfig";

export interface BlockOptions {
  x: number;
  y: number;
  width: number;
  height: number;
  color: number;
  isDarumaHead?: boolean;
  index: number;
}

export class Block {
  public readonly body: MatterJS.BodyType;
  public readonly index: number;
  public readonly isDarumaHead: boolean;
  public isEscaping: boolean = false;
  public isRemoved: boolean = false;

  private scene: Phaser.Scene;
  private graphics: Phaser.GameObjects.Graphics;
  private color: number;
  private width: number;
  private height: number;

  constructor(scene: Phaser.Scene, options: BlockOptions) {
    this.scene = scene;
    this.index = options.index;
    this.isDarumaHead = options.isDarumaHead ?? false;
    this.color = options.color;
    this.width = options.width;
    this.height = options.height;

    // Create Matter.js body via Phaser
    const matterBody = scene.matter.add.rectangle(
      options.x,
      options.y,
      options.width,
      options.height,
      {
        friction: StageConfig.blockFriction,
        restitution: StageConfig.blockRestitution,
        frictionAir: 0.01,
        isStatic: false,
        label: this.isDarumaHead ? "darumaHead" : `block_${options.index}`,
      }
    );

    this.body = matterBody as MatterJS.BodyType;

    // Create visual representation
    this.graphics = scene.add.graphics();
    this.draw();
  }

  /** Redraw block at current physics position */
  draw(): void {
    this.graphics.clear();

    const pos = this.body.position;
    const angle = this.body.angle;

    this.graphics.save();
    this.graphics.setPosition(pos.x, pos.y);
    this.graphics.setRotation(angle);

    if (this.isDarumaHead) {
      // Draw daruma head
      this.graphics.fillStyle(0xf5f5dc, 1); // beige
      this.graphics.fillEllipse(0, 0, this.width, this.height);
      // Eyes
      this.graphics.fillStyle(0x000000, 1);
      this.graphics.fillCircle(-12, -5, 5);
      this.graphics.fillCircle(12, -5, 5);
      // Mouth
      this.graphics.fillStyle(0xcc3333, 1);
      this.graphics.fillEllipse(0, 10, 15, 8);
    } else {
      // Draw colored block with rounded corners effect
      this.graphics.fillStyle(this.color, 1);
      this.graphics.fillRoundedRect(
        -this.width / 2,
        -this.height / 2,
        this.width,
        this.height,
        6
      );
      // Block border
      this.graphics.lineStyle(2, 0xffffff, 0.3);
      this.graphics.strokeRoundedRect(
        -this.width / 2,
        -this.height / 2,
        this.width,
        this.height,
        6
      );
    }

    this.graphics.restore();
  }

  /** Get center X position */
  getX(): number {
    return this.body.position.x;
  }

  /** Get center Y position */
  getY(): number {
    return this.body.position.y;
  }

  /** Get angle in radians */
  getAngle(): number {
    return this.body.angle;
  }

  /** Apply X-direction impulse */
  applyImpulseX(forceX: number): void {
    this.scene.matter.body.applyForce(this.body, this.body.position, {
      x: forceX,
      y: 0,
    });
    this.isEscaping = true;
  }

  /** Set static (for ground block) */
  setStatic(isStatic: boolean): void {
    this.scene.matter.body.setStatic(this.body, isStatic);
  }

  /** Remove from scene and physics world */
  destroy(): void {
    this.isRemoved = true;
    this.graphics.destroy();
    this.scene.matter.world.remove(this.body);
  }

  /** Update visual to match physics */
  update(): void {
    if (!this.isRemoved) {
      this.draw();
    }
  }
}
